/**
 * Decision Vocabulary — judgment, disposition, eligibility and failure (D2)
 *
 * ═══════════════════════════════════════════════════════════════════════════════════════
 * Four different things, four different fields. This module owns the words for all four.
 * ═══════════════════════════════════════════════════════════════════════════════════════
 *
 * Before this existed, `skip` meant all of:
 *
 *   1. Marva judged that silence was better           → a JUDGMENT (`wait`)
 *   2. A frequency cap withheld a proposed send       → a DISPOSITION (`blocked`)
 *   3. The signal was never a decision-worthy moment  → ELIGIBILITY (`ineligible`)
 *   4. Bedrock threw, or the reply would not parse    → a FAILURE (`decisionFailure`)
 *
 * So the vision's headline restraint metric — the share of eligible moments where Marva
 * correctly chose not to intervene — was not computable, and a parse error IMPROVED it.
 * Splitting them makes three independent measures possible:
 *
 *   restraint           = wait / (help + wait)                    ← judgment only
 *   governance load     = policy blocks / policy evaluations      ← disposition only
 *   decision reliability= successful judgments / eligible attempts ← failure only
 *
 * ── Why the vocabularies are separate exports, not one enum ──────────────────────────
 *
 * A reason code is only meaningful against the axis it belongs to. `cooldown` is a policy
 * disposition and can never be an eligibility outcome; `sensor_floor` is the reverse. One
 * flat enum would let a call site file a policy block as a signal-quality problem, which is
 * exactly the collapse this module exists to undo. Keeping them apart makes that a lookup
 * error rather than a plausible value.
 *
 * Governance reasons are deliberately NOT redefined here — they are the `BLOCK_REASONS`
 * vocabulary from `@marva/shared/block-reasons` (which `governance-enforcer` re-exports),
 * and `checkGovernance` stays the single enforcement point. This module records the reason
 * the enforcer returned; it does not fork frequency, cooldown, fatigue, quiet-hour or
 * suppression logic.
 *
 * @module decision-vocabulary
 */

// From `./block-reasons`, NOT from `./governance-enforcer`: this module is reachable from
// `schemas.js`, and the enforcer builds a DynamoDB client at load. Importing it here made
// describing a table instantiate an AWS client everywhere.
const { BLOCK_REASONS } = require('./block-reasons');
// Dependency-free: the one send-hold predicate owns the disabled-workspace reason (F-24).
const {
  WORKSPACE_DISABLED_REASON, WORKSPACE_SHADOWED_REASON, WORKSPACE_UNREADABLE_REASON, WORKSPACE_DELETED_REASON,
  MODE_DISPOSITION_SOURCE
} = require('./send-hold');

// ── 1. Judgment — what Marva chose, for a person, on evidence ──────────────────────────

/**
 * `help` replaces legacy `engage`. Channel, intent, timing and offer are separate details
 * of the proposed help, not part of the judgment itself.
 *
 * `wait` replaces both the model's own `wait` and a deliberate legacy `skip`. It is
 * TERMINAL: nothing is proposed, so nothing can be blocked or deferred, and no executor
 * may run. A later trigger creates a new decision.
 *
 * `escalate` is valid data now but executable only through an existing authority contract
 * until O1 item 6 (authority as data) lands.
 */
const JUDGMENT_KINDS = Object.freeze(['help', 'wait', 'escalate']);

/**
 * Who made the call. `deterministic_contract` covers the paths that synthesise a decision
 * without asking the model at all — the chat fast path and the comparison handler — so
 * their rows are never mistaken for the agent's own judgment when restraint is measured.
 */
const JUDGMENT_SOURCES = Object.freeze(['agent', 'deterministic_contract']);

/**
 * Triggers whose decision row IS a turn of a conversation the SHOPPER started — the chat fast
 * path logs one `customer_message:chat` row for every message the shopper sends (MAR-242).
 *
 * The one exported set behind both of a reply turn's exclusions:
 *   - CONTACT COUNTING (`countsAsContact`, outcome-vocabulary): the reply reached the shopper,
 *     but it is their own message answered, not Marva reaching out — so it starts no cooldown,
 *     spends no cap and fatigues nothing. Before this a shopper who merely chatted reset their
 *     own cooldown and blocked the next cadence send;
 *   - OUTREACH LISTS (`recentOutreachLines`, the envelope and `customer-decisions` reads): the
 *     conversation is already in the prompt as the conversation, so these rows are omitted
 *     rather than rendered as "Sent customer_message via chat" and left to spend row limits.
 *
 * ⚠️ Keyed on the TRIGGER — who started the exchange — deliberately NOT on `judgment.source`,
 * which records who WEIGHED the decision (the restraint denominator's question). The two
 * coincide for the chat fast path only by accident: a future templated proactive sender would
 * naturally declare `deterministic_contract`, and must not vanish from every cap by doing so.
 *
 * `comparison` is deliberately NOT here. It is an email in the shopper's inbox, sent when the
 * onsite agent's `send_email` tool is called — which the model can do repeatedly — so it keeps
 * spending the email frequency cap. It is cooldown-exempt instead (`COOLDOWN_EXEMPT_TRIGGERS`),
 * so earlier outreach never holds back the email the shopper just asked for.
 *
 * Adding a trigger here removes it from every frequency cap. It is a vocabulary change and is
 * pinned by `decision-vocabulary.test.js`.
 */
const REPLY_TURN_TRIGGERS = Object.freeze(['customer_message']);

/**
 * True when this decision row is a shopper-started conversation turn. See `REPLY_TURN_TRIGGERS`.
 *
 * Reads `trigger`, or `campaignType` on a touchpoint projection (`mapDecisionToTouchpoint` names
 * the trigger that), so the same row answers the same way in either shape.
 */
function isReplyTurnDecision(row) {
  if (!row || typeof row !== 'object') return false;
  return REPLY_TURN_TRIGGERS.includes(row.trigger || row.campaignType);
}

// ── 2. Disposition — what policy and execution then did about it ───────────────────────

const DISPOSITION_KINDS = Object.freeze(['executed', 'blocked', 'deferred']);

/**
 * Which authority produced the disposition. `feasibility` is the one that used to be
 * invisible: a guardrail deciding the chosen channel cannot carry the message is not the
 * agent changing its mind, and before this it overwrote the judgment in place.
 */
const DISPOSITION_SOURCES = Object.freeze([
  'governance', // the enforcer withheld it — reason is a BLOCK_REASONS value
  'authority', // an authority rung held it for the merchant
  'feasibility', // a code-enforced guardrail found the proposed help unusable
  'scheduler', // timing moved it, rather than policy
  // an execution-time re-check found a fact the judgment rested on is no longer true
  // (MAR-69). Not governance — no rule about the shopper fired — and not scheduler — timing
  // did not move it. Reason is `LIFECYCLE_REASONS.CONTEXT_STALE`, or (MAR-411)
  // `LIFECYCLE_REASONS.SELECTION_UNAVAILABLE` when the selected products emptied at execution.
  'revalidation',
  // the workspace's MODE withheld it — an admin-disabled workspace sends nothing (F-24). Not
  // governance (no rule about the shopper fired, so it stays out of the caps populations and
  // governance load) and not authority (the merchant's grant did not decide it). Reason is a
  // `MODE_REASONS` value. Shadow is NOT this: a shadowed decision is `shadowed`, not blocked.
  MODE_DISPOSITION_SOURCE
]);

/**
 * Mode reasons — a send the workspace's mode withheld (`source: 'mode'`), from
 * `@marva/shared/send-hold`. Recorded `suppressed`, never `failed`, never a contact.
 */
const MODE_REASONS = Object.freeze({
  WORKSPACE_DISABLED: WORKSPACE_DISABLED_REASON,
  /** a live-dispatched deferred send whose channel was shadowed by re-drive time */
  WORKSPACE_SHADOWED: WORKSPACE_SHADOWED_REASON,
  /** the workspace row could not be read, so the send failed closed */
  WORKSPACE_UNREADABLE: WORKSPACE_UNREADABLE_REASON,
  /**
   * MAR-481: a strongly consistent read found NO workspace row — the workspace was deleted while
   * this decision was queued (a scheduled replay, a quiet-hours re-defer). Terminal, unlike
   * `workspace_unreadable` (which re-defers). Withheld `suppressed`, never `failed`, never a
   * contact — before this it went down the live path with the WIDEST default authority.
   */
  WORKSPACE_DELETED: WORKSPACE_DELETED_REASON,
  /**
   * MAR-425: a change-derived occasion class whose LIVE release flag is not `"true"` (`MEMORY_REMATCH_LIVE_ENABLED`)
   * reached the send on a live workspace — a scheduled replay of a decision made in shadow, a
   * re-defer after ops turned the flag off. The same string as the pre-judgment refusal: one fact,
   * read at two points. Withheld `suppressed`, never `failed`, never a contact.
   */
  OCCASION_RELEASE_DARK: 'occasion_release_dark'
});

/**
 * Feasibility reasons — a guardrail, not a policy and not a judgment.
 *
 * `CONTACT_CAPTURE_UNUSABLE` is the one that motivated the axis: `enforceChannelPolicy`
 * used to rewrite `action` to `skip` when contact_capture could not be shown and no email
 * fallback was permitted, which reads downstream as Marva choosing restraint.
 *
 * `EMPTY_MESSAGE` is a TECHNICAL failure that has been filed as governance `suppressed`
 * since generation could return no copy — see `docs/work/agent-empty-engage.md`. It is
 * named here so the reclassification is greppable, but it is recorded as a
 * `decisionFailure`, not a disposition. See `DECISION_FAILURE_CODES`.
 */
const FEASIBILITY_REASONS = Object.freeze({
  CONTACT_CAPTURE_UNUSABLE: 'contact_capture_unusable',
  NO_CONTACT_AVAILABLE: 'no_contact_available',
  UNKNOWN_CHANNEL: 'unknown_channel',
  /**
   * The compliance floor for commercial email (MAR-330): the send site found the message
   * could not lawfully go out — the workspace has no postal address to put in the footer, or
   * no unsubscribe link could be built. Feasibility, not governance: no rule about the SHOPPER
   * fired, the channel simply cannot carry this message yet. Written by `shopper-email` as
   * `suppressed` + `{blocked, source:'feasibility'}`, so it never counts as a contact.
   */
  NO_POSTAL_ADDRESS: 'no_postal_address',
  NO_UNSUBSCRIBE_URL: 'no_unsubscribe_url',
  /** The workspace cannot currently provide an SES sender identity. */
  NO_DOMAIN_CONFIGURED: 'no_domain_configured',
  /** The configured SES sender identity has not completed verification. */
  DOMAIN_NOT_VERIFIED: 'domain_not_verified',
  /** The configured SES sender identity has expired. */
  DOMAIN_EXPIRED: 'domain_expired',
  /** A synthetic workspace tried to address a recipient outside its explicit test sinks. */
  SIMULATOR_RECIPIENT_BLOCKED: 'simulator_recipient_not_allowlisted',
  /**
   * An email decision whose trigger has no email template (MAR-331, spec §4.6) — e.g. an
   * engage on `confusion_signal` routed to email. Refused at the gateway's send site BEFORE
   * anything is published, as `suppressed` + `{blocked, source:'feasibility'}`, so it never
   * counts as a contact. It used to be rendered as a cart-recovery email (the router's
   * `|| 'cart_recovery'` fallback) and die downstream as `failed: cart_unavailable`.
   */
  NO_EMAIL_TEMPLATE: 'no_email_template',
  /**
   * An email decision whose INTENT asked for a type this trigger's payload cannot feed, on a
   * trigger with no template of its own to fall back to (MAR-264) — e.g. `recover_cart` on
   * `confusion_signal`: no cart travels with that trigger, so no cart-recovery email can be
   * composed. Refused at the gateway's send site BEFORE anything is published, as `suppressed`
   * + `{blocked, source:'feasibility'}`. It used to be published anyway and dropped by the
   * generator (`'skipped'`), leaving the decision `dispatched` — counted as a contact.
   */
  EMAIL_INTENT_UNROUTABLE: 'email_intent_unroutable',
  /**
   * The email type was routable, but the payload the gateway actually BUILT lacks an input that
   * type's generator cannot compose without (`missingEmailInputs`, MAR-264 review) — e.g. an
   * `comparison` trigger that carried `products: []` (first seen on the retired `inspiration`
   * trigger). Refused at the gateway BEFORE the
   * discount mint, governance and any publish, as `suppressed` + `{blocked,
   * source:'feasibility'}`: the moment carried nothing to write about; nothing broke.
   */
  EMAIL_PAYLOAD_INCOMPLETE: 'email_payload_incomplete',
  /**
   * A browse follow-up whose viewed products can no longer be shown — every one is gone,
   * draft/archived or out of stock (MAR-264 review). Settled by `shopper-email` at compose as
   * `suppressed` + `{blocked, source:'feasibility'}`; it used to return a bare `'skipped'` that
   * left the decision `dispatched`, counted as a contact.
   */
  NO_FEATURABLE_PRODUCTS: 'no_featurable_products',
  /**
   * The workspace's hourly email send cap (ESP E4, `HOURLY_SEND_CAP` / the domain row's
   * `hourlySendCap`) was full when a decision's email reached the send site (PR #263 review).
   * A per-WORKSPACE volume guardrail protecting shared SES reputation — not a rule about the
   * shopper — so it is feasibility, never governance: it stays out of the MAR-55 caps
   * populations, which count a person. Written by `shopper-email` as `suppressed` +
   * `{blocked, source:'feasibility'}`, so it never counts as a contact. It used to leave the
   * row `scheduled` and the decision `dispatched` (a contact), with nothing re-triggering it.
   * Only a cap actually reached: a counter that could not be READ is not this.
   */
  HOURLY_SEND_CAP: 'hourly_send_cap',
  /**
   * MAR-125: an SMS whose copy was written around an offer (`{discount}`) that the mint then
   * did not issue — refused, over the ceiling the mint resolved, or out of budget. The copy
   * cannot be sent without the offer it promises. Written by `sms-sender` as `suppressed` +
   * `{blocked, source:'feasibility'}`, so it never counts as a contact (it used to leave the
   * decision `dispatched`).
   */
  OFFER_NOT_ISSUED: 'offer_not_issued',
  /**
   * MAR-125: an SMS whose copy names a percent off that the minted code does not back — no
   * percent code at all, or a number that cannot be shown to be the decision's own offer
   * (`reconcileOfferCopy`). Withheld rather than rewritten, because the number may be the
   * merchant's own sale. Written by `sms-sender` like OFFER_NOT_ISSUED.
   */
  OFFER_COPY_UNBACKED: 'offer_copy_unbacked'
});

/**
 * Executor self-restraint — the executor declining on its own, which is neither a
 * governance rule nor a judgment. Both values were already being written into the free-text
 * `suppressionReason` field with no vocabulary behind them.
 */
const SELF_RESTRAINT_REASONS = Object.freeze({
  ALREADY_FULLY_OPTED_IN: 'already_fully_opted_in',
  ALREADY_OFFERED_THIS_SESSION: 'already_offered_this_session'
});

/**
 * Lifecycle reasons — a disposition produced by where the decision is in its life rather
 * than by a rule about the shopper. `SCHEDULED`/`ENQUEUED` are not withholds; `ESCALATION_PENDING`
 * is the high-value hold waiting on the merchant. All three were free strings until item 4.
 */
const LIFECYCLE_REASONS = Object.freeze({
  SCHEDULED: 'scheduled',
  ENQUEUED: 'enqueued',
  ESCALATION_PENDING: 'escalation_pending',
  /** The last-resort default when an enforcer block arrives without a typed reason. */
  GOVERNANCE_BLOCKED: 'governance_blocked',
  /**
   * A scheduled/replayed send re-checked the volatile facts its judgment rested on (a price
   * drop, a restock, a live sale, the items in play) and one is no longer true, so it was
   * withheld (MAR-69, one-context-contract §4.4). Recorded as `suppressed` with disposition
   * `{blocked, source:'revalidation'}` — nothing broke and nothing was retired. There is NO
   * re-emit: the next sensor signal makes a new decision, as for every other withhold.
   * Deliberately NOT a `BLOCK_REASONS` value: the governance enforcer never emits it.
   */
  CONTEXT_STALE: 'context_stale',
  /**
   * MAR-411: a help on a product-selection trigger whose executable selected set was EMPTY at
   * execution — every product the agent selected was excluded, unavailable, not a candidate, or
   * found unavailable by the last-moment availability re-read (or the decision named none).
   * Nothing is composed or sent. Recorded as `suppressed` with disposition `{blocked,
   * source:'revalidation'}` and the per-product evidence on the row as `selectionExecution`.
   * Never `failed` (nothing broke) and never relabelled as the agent's `wait` (the judgment was
   * help). Not a `BLOCK_REASONS` value: no rule about the shopper fired.
   */
  SELECTION_UNAVAILABLE: 'selection_unavailable'
});

/**
 * Authority reasons — why the rung refused, or why it is asking (D7, O1 item 6).
 *
 * ⚠️ These are NOT `BLOCK_REASONS` values and they never carry `source: 'governance'`. An
 * authority refusal means the merchant never granted this, which is a different fact from a
 * rule firing — exactly the distinction `DISPOSITION_SOURCES.authority` was reserved for.
 *
 * **Three of the four are silent, and one is not.** A REFUSAL — `ABOVE_RUNG`, `CLASS_OFF`,
 * `CONFIG_UNAVAILABLE` — emails nobody and queues nothing for approval: it produces the same
 * silence a disabled trigger produces today, which is what made the migration off the
 * per-campaign booleans behaviour-identical.
 *
 * `ABOVE_CEILING` is the exception and it is deliberate (phase 1″, §6.6: *bounds escalate;
 * they do not refuse*). An over-ceiling discount is not a thing the merchant declined — it is a
 * judgment call only they can make, and clamping it silently to the ceiling throws that judgment
 * away without ever showing them the reason the agent had for the larger number. So this one
 * value ASKS: the enforcer returns it with `escalate: true` and `blocked: false`, and the caller
 * routes the proposal into the escalation lifecycle (`scheduled-executor`) instead of sending.
 *
 * It is still a `BLOCKED_REASONS` member below because the authority hold persists this value as
 * its disposition reason. The generic cart-value/LTV hold persists
 * `LIFECYCLE_REASONS.ESCALATION_PENDING`; the distinction lets the receipt name the boundary
 * while the scheduled drain keeps one operational lifecycle result.
 *
 * ⚠️ **And the escalation itself ships DARK** — `AUTHORITY_ESCALATION_ENABLED` on
 * `engagement-gateway`, default false, because the outbound wire discards the agent's percent
 * (F-32). With the flag off the enforcer still returns this value and the caller still counts it;
 * it just sends the clamped offer instead of asking. The vocabulary is live; the interruption is
 * not.
 *
 * ⚠️ **It reaches the OUTBOUND channels only** (email + SMS — both call the gateway's
 * `governOutboundSend` since MAR-157, closing F-11). On-site channels never reach it. The
 * every-channel count of over-ceiling proposals is `AuthorityCeilingExceeded`, emitted at decision
 * time by `decision-logger`; the send-site `AuthorityEscalationWouldFire` is outbound-only and
 * post-governance and must never be reported as a fleet rate.
 */
const AUTHORITY_REASONS = Object.freeze({
  /** The action's class needs a higher rung than this workspace is on. */
  ABOVE_RUNG: 'above_rung',
  /** The merchant has switched this class off for this workspace. */
  CLASS_OFF: 'class_off',
  /** The authority config could not be read, so a gated class fails CLOSED. */
  CONFIG_UNAVAILABLE: 'authority_config_unavailable',
  /**
   * The proposed discount is above the merchant's own ceiling. The ONLY non-silent value here:
   * it escalates rather than refusing, and it never clamps.
   */
  ABOVE_CEILING: 'above_ceiling'
});

/**
 * Execution deduplication — a replayed send whose claim was denied, or a Shopify
 * side-effect fence. Per the spec's settled decisions this is infrastructure, NOT restraint
 * and NOT a governance block: it is associated with the original decision and creates no
 * second judgment. PR #53 (gateway send idempotency) consumes this class.
 */
const DEDUPLICATION_REASONS = Object.freeze({
  DUPLICATE_EXECUTION: 'duplicate_execution',
  SHOPIFY_SIDE_EFFECT_FENCE: 'shopify_side_effect_fence'
});

/**
 * Every value that may legitimately appear on a `blocked` disposition, from whichever
 * authority produced it. `suppressionReason` on the decision row is a free string with
 * three unrelated producers today (BLOCK_REASONS, executor self-restraint, and a hardcoded
 * `'governance_blocked'` literal at five sites); this is the set that replaces it.
 */
const BLOCKED_REASONS = Object.freeze([
  ...Object.values(BLOCK_REASONS),
  ...Object.values(FEASIBILITY_REASONS),
  ...Object.values(SELF_RESTRAINT_REASONS),
  ...Object.values(LIFECYCLE_REASONS),
  ...Object.values(AUTHORITY_REASONS),
  ...Object.values(MODE_REASONS)
]);

// ── 3. Eligibility — was there a decision-worthy moment at all? ─────────────────────────

/**
 * `eligible`                → a moment worth a judgment; it enters the restraint denominator.
 * `ineligible`              → the signal was never a valid moment. Signal coverage, not policy.
 * `blocked_before_judgment` → policy stopped us BEFORE asking the model, for cost or safety.
 *                             Observable, counted as governance load, and excluded from
 *                             restraint — because Marva never made a judgment to be restrained.
 */
const ELIGIBILITY_KINDS = Object.freeze(['eligible', 'ineligible', 'blocked_before_judgment']);

/**
 * Ineligible reasons — a threshold deciding the sensor produced no valid moment.
 *
 * ⚠️ A threshold may decide whether there is a moment. It may NEVER decide what Marva
 * should do for an eligible person — that is what makes it eligibility rather than a fourth
 * judgment kind. If you find yourself wanting a reason here that describes the SHOPPER
 * rather than the SIGNAL, it belongs on the other axis.
 *
 * `ALREADY_RESOLVED` is the subtle one. A recent purchase can be either a factual
 * invalidation of the occasion (the abandoned cart was bought — there is no moment left) or
 * evidence for a judgment. Each call site must declare which it means; parsing the reason
 * text to find out is forbidden.
 */
const INELIGIBLE_REASONS = Object.freeze({
  TRIGGER_DISABLED: 'trigger_disabled',
  DUPLICATE_SESSION: 'duplicate_session',
  DUPLICATE_OCCASION: 'duplicate_occasion',
  SENSOR_FLOOR: 'sensor_floor',
  ALREADY_RESOLVED: 'already_resolved',
  MALFORMED_TRIGGER: 'malformed_trigger',
  // MAR-423: the occurrence names nobody Marva can reason about — a guest order, or a customer
  // id with no resolvable person behind it. A fact about the SIGNAL (it carries no person), not
  // about a shopper, so it is signal coverage.
  UNRESOLVED_PERSON: 'unresolved_person'
});

/**
 * Pre-decision policy reasons — checks kept before the model for cost or safety.
 *
 * These are POLICY, not signal quality, even though they run before any judgment exists.
 * They belong to governance load. `MAU_CAP` is a plan limit and `SESSION_VOLUME_CAP` is a
 * frequency cap scoped to one session; both are caps, and caps are policy.
 */
const PRE_JUDGMENT_BLOCK_REASONS = Object.freeze({
  MAU_CAP: 'mau_cap',
  // An administrator stopped the workspace before any decision existed. Once a decision has
  // been made, the same fact is a mode-sourced disposition instead (MODE_REASONS).
  WORKSPACE_DISABLED: WORKSPACE_DISABLED_REASON,
  FREQUENCY_CAP: BLOCK_REASONS.FREQUENCY_CAP_WEEK,
  COOLDOWN: BLOCK_REASONS.COOLDOWN,
  SESSION_VOLUME_CAP: 'session_volume_cap',
  // MAR-423: the person is erased — known before the model, so the model is never paid for. The
  // executor's own MAR-345 check stays the fence; this is the cost-side read of the same fact.
  SUBJECT_ERASED: BLOCK_REASONS.SUBJECT_ERASED,
  // MAR-425: the erasure fence itself could not be read before judgment, for an occasion that
  // fails CLOSED on it (memory_rematch). The executor's own label for the same fact.
  ERASURE_UNREADABLE: BLOCK_REASONS.SUPPRESSION_CHECK_FAILED,
  // MAR-423: a live workspace with no authority row, for an occasion no legacy flag gates. The
  // enforcer would read the WIDEST config for it, so nothing the merchant set would bound the
  // send; refused before judgment instead. A pre-judgment policy reason in its own vocabulary —
  // deliberately NOT an `AUTHORITY_REASONS` value, which only a judged decision's disposition
  // carries (CLAUDE.md: never merge the two in a metric).
  AUTHORITY_UNCONFIGURED: 'authority_unconfigured',
  // MAR-423 (spec §6.4, "an occasion that cannot be sent must not pay for a decision"): the
  // occasion's action class is refused by the merchant's authority (an `off` exception, or a rung
  // that does not cover it), known from the trigger before the model. Pre-judgment vocabulary —
  // never an `AUTHORITY_REASONS` value; the send-time enforcer stays the backstop.
  AUTHORITY_CLASS_CLOSED: 'authority_class_closed',
  // MAR-423: the authority row exists and does not parse (`AuthoritySchema`). A retry cannot fix
  // a corrupt row, so this is refused before judgment rather than looped to the DLQ; a READ that
  // failed still throws and retries.
  AUTHORITY_MALFORMED: 'authority_malformed',
  // The monthly email cap, labelled as itself rather than as the weekly one.
  FREQUENCY_CAP_MONTH: BLOCK_REASONS.FREQUENCY_CAP_MONTH,
  // MAR-425: a change-derived occasion class whose LIVE release flag is off — an ops flag
  // (`MEMORY_REMATCH_LIVE_ENABLED`), not a merchant setting and not authority. Refused before the
  // model so a live workspace pays nothing; shadow workspaces are unaffected. Pre-judgment
  // vocabulary only — never a `BLOCK_REASONS` or `AUTHORITY_REASONS` value.
  OCCASION_RELEASE_DARK: 'occasion_release_dark'
});

// ── 4. Technical failure — nothing about the shopper is known ──────────────────────────

/**
 * Where it broke. A failure produces NO judgment: the point of the axis is that a Bedrock
 * timeout must never be counted as Marva choosing to wait.
 */
const DECISION_FAILURE_STAGES = Object.freeze([
  'context', // assembling the context threw
  'model', // Bedrock refused, timed out or errored
  'parse', // the reply was not usable JSON, or carried no valid judgment
  'persistence', // the decision was made and the write was lost
  'routing', // an exhaustive router met a value it did not know
  // The decision was made, and the composer could not load the exact context it was made
  // from (MAR-69, one-context-contract §4.5). Like `routing`/`persistence` it happens AFTER a
  // judgment, so it may sit beside one — the agent judged `help`, and our own machinery then
  // could not carry that judgment into words without re-reading the shopper.
  'composition'
]);

/** Stable, non-PII codes. Never interpolate an error message into one of these. */
const DECISION_FAILURE_CODES = Object.freeze({
  CONTEXT_ASSEMBLY_FAILED: 'context_assembly_failed',
  MODEL_INVOCATION_FAILED: 'model_invocation_failed',
  RESPONSE_UNPARSEABLE: 'response_unparseable',
  /**
   * The reply was unusable AND Bedrock says generation stopped at `max_tokens` (MAR-404). An
   * envelope that merely fails to balance without that stop reason is `response_unparseable`
   * (an unescaped `"` does exactly that). Stage `parse`. Distinct from `response_unparseable` because the
   * cause and the fix differ: a token budget, not a format the model got wrong. Recorded only
   * after the one bounded repair also failed; `decisionFailure.detail` says how it ended.
   */
  RESPONSE_TRUNCATED: 'response_truncated',
  INVALID_JUDGMENT: 'invalid_judgment',
  DECISION_WRITE_LOST: 'decision_write_lost',
  UNROUTABLE_JUDGMENT: 'unroutable_judgment',
  /**
   * Generation returned no copy for a message-bearing channel. Recorded as a FAILURE
   * because nothing was withheld on purpose — see `docs/work/agent-empty-engage.md`. It was
   * `status: 'suppressed'` (i.e. governance) until item 4.
   */
  EMPTY_MESSAGE: 'empty_message',
  /**
   * The decision's context artifact could not be loaded, or did not verify (missing,
   * unreadable, schema-invalid, hash or context-id mismatch). Stage `composition`. There is
   * no fresh-assembly fallback (§4.5): an email written from different memory than the
   * decision was made from is exactly what the handoff exists to prevent. The loader's own
   * code (`artifact_missing` | …) rides beside it, never inside it.
   */
  CONTEXT_ARTIFACT_UNAVAILABLE: 'context_artifact_unavailable',
  /**
   * A safety-critical composition block threw while rendering the decision's shopper-facing
   * message (MAR-363). Stage `composition`. The block name belongs in `detail`; no model is
   * called and no message is sent from the incomplete prompt.
   */
  CONTEXT_RENDER_FAILED: 'context_render_failed',
  /**
   * The send could not learn the shop's currency because the workspace read itself failed
   * (MAR-261). Stage `composition`. Guessing `'USD'` instead printed a GBP store's prices in
   * `$`; a missing row or a row without a currency is NOT this — those still send, with
   * `currency: null` and bare figures (never USD), and a warning (MAR-262).
   */
  WORKSPACE_READ_FAILED: 'workspace_read_failed',
  /**
   * The cart a cart-recovery email needs — its `cartId`, or the cart's items — is not
   * available, so no email can be composed (MAR-278). Stage `composition`. Recorded as a
   * FAILURE so the row ends `failed` rather than being left `dispatched` with nothing sent.
   */
  CART_UNAVAILABLE: 'cart_unavailable',
  /**
   * A decision's email has a usable occasion payload but no recipient address. Stage
   * `composition`. This is deliberately distinct from `cart_unavailable`: the cart exists;
   * the contact door does not (MAR-284).
   */
  CONTACT_UNAVAILABLE: 'contact_unavailable',
  /**
   * A decision's email reached its generator without an input that email type cannot be
   * composed without (`@marva/shared/email-routing` `missingEmailInputs`, MAR-264) — e.g. a
   * `browse_followup` with no `sessionId`. Stage `composition`. The gateway routes only to types
   * a trigger feeds, so this is the generator's backstop for a router/payload that drifted:
   * recorded as a FAILURE, never a bare `'skipped'` that leaves the decision `dispatched`
   * (a contact for an email nobody sent). Cart recovery keeps its own `cart_unavailable`.
   */
  EMAIL_INPUT_MISSING: 'email_input_missing'
});

// ── 5. Legacy compatibility ────────────────────────────────────────────────────────────

/**
 * What a legacy row's `skip` cannot be resolved into.
 *
 * A bare `skip` with no structured provenance may have meant restraint, a policy block, an
 * infeasible channel or a crashed model invocation. There is no way to tell after the fact,
 * and guessing from the `reasoning` prose is forbidden — that is how
 * `categorizeSkipReason` came to classify judgments by regex.
 *
 * So it is named, displayable, and excluded from every restraint and OPE denominator. An
 * honest gap in the history beats a fabricated one.
 */
const LEGACY_AMBIGUOUS = 'legacy_ambiguous';

/**
 * The legacy `action` value that meant "help". READ-ONLY history: since MAR-65 nothing writes
 * `action` on a decision row, but every row written before the cutover still carries it (and
 * pre-2026-09-08 rows carry nothing else), so readers keep recognising it. Exported so the
 * one read-side mapping (`normalizeDecisionRead`, and the logger's replay canonicalisation)
 * cannot drift apart.
 */
const LEGACY_HELP_ACTION = 'engage';

/** Statuses that mean a proposed help was withheld rather than sent. */
const WITHHELD_STATUSES = Object.freeze(['suppressed']);

/** Statuses that mean the help was handed off or confirmed. */
const SENT_STATUSES = Object.freeze(['dispatched', 'executed', 'delivered', 'pending_delivery']);

/**
 * Read a decision row — canonical or legacy — as judgment + disposition.
 *
 * This is the ONE normalizer. Consumers migrating off `action`/`status` call this rather
 * than growing their own mapping, because the interesting case is not the happy path but
 * what each of them decides to do with an ambiguous row, and that decision should be made
 * once.
 *
 * Deliberately NOT a backfill: it computes a read-time view and writes nothing. No historical
 * row is rewritten, because rewriting one would require the guess this function refuses to make.
 *
 * @param {object} row a decision row, canonical or legacy
 * @returns {{judgment: string|null, disposition: object|null, ambiguous: boolean, source: 'canonical'|'legacy'}}
 */
function normalizeDecisionRead(row) {
  if (!row) {
    return { judgment: null, disposition: null, ambiguous: true, source: 'legacy' };
  }

  // Canonical rows answer for themselves.
  if (row.judgment && row.judgment.kind) {
    return {
      judgment: row.judgment.kind,
      disposition: row.disposition || null,
      ambiguous: false,
      source: 'canonical'
    };
  }

  const action = row.action;
  const status = row.status;

  // An explicit legacy `wait` is unambiguous — the model said so. These rows exist because
  // the parser accepted `wait` for months while `DecisionSchema.action` forbade it and
  // nothing validated the write.
  if (action === 'wait') {
    return { judgment: 'wait', disposition: null, ambiguous: false, source: 'legacy' };
  }

  if (action === LEGACY_HELP_ACTION) {
    return {
      judgment: 'help',
      disposition: legacyDispositionFor(status, row.suppressionReason),
      ambiguous: false,
      source: 'legacy'
    };
  }

  // `skip` and `defer`: the collapse this whole item exists to undo.
  return { judgment: null, disposition: null, ambiguous: true, source: 'legacy' };
}

/**
 * Did Marva judge HELP on this row? The one predicate every contact reader asks (MAR-65).
 *
 * Canonical rows answer through `judgment.kind`; a pre-cutover row with only the legacy
 * `action: 'engage'` answers through `normalizeDecisionRead`'s legacy branch — so both a
 * new row that carries no `action` at all and a 2026-08 row that carries nothing BUT `action`
 * count. Before this, the caps, cooldown, frequency and fatigue readers each tested
 * `action === 'engage'` themselves, and deleting the mirror would have made every one of them
 * fail OPEN: nothing counted as a contact, so nothing capped.
 *
 * This is the JUDGMENT, not proof of contact. A help the enforcer withheld is still a help;
 * callers pair it with `reachedCustomer` (outcome-vocabulary) exactly as they paired the old
 * `action` test. A `wait`, an `escalate`, a failed decision (no judgment) and an ambiguous
 * legacy `skip` are all false.
 *
 * @param {object} row - a decision row (canonical or legacy) or a touchpoint projection of one
 * @returns {boolean}
 */
function isHelpDecision(row) {
  if (!row || typeof row !== 'object') return false;
  return normalizeDecisionRead(row).judgment === 'help';
}

/** Map a legacy lifecycle status onto a disposition, for rows that carried a real proposal. */
function legacyDispositionFor(status, suppressionReason) {
  if (!status) return null;
  if (WITHHELD_STATUSES.includes(status)) {
    return {
      kind: 'blocked',
      source: 'governance',
      reasonCode: suppressionReason || LEGACY_AMBIGUOUS,
      until: null
    };
  }
  if (status === 'scheduled') {
    return { kind: 'deferred', source: 'scheduler', reasonCode: 'scheduled', until: null };
  }
  if (SENT_STATUSES.includes(status)) {
    return { kind: 'executed', source: 'scheduler', reasonCode: status, until: null };
  }
  // cancelled / expired / failed / shadowed / escalated are lifecycle facts, not
  // dispositions. They are left null rather than dressed up as policy.
  return null;
}

/**
 * True when this row may enter the restraint denominator — `wait / (help + wait)`.
 *
 * Excluded, each for a different reason and none of them restraint: ambiguous legacy rows,
 * rows blocked before any judgment was made, rows that failed, and rows whose judgment came
 * from a deterministic contract rather than from the agent weighing the choice.
 */
function countsTowardRestraint(row) {
  if (!row) return false;
  // A failure that stands IN PLACE of a judgment is excluded — nothing was chosen. A failure
  // that sits BESIDE one (a `composition`/`routing` defect after the agent judged `help`) is
  // an execution fact, not a judgment fact: the agent still weighed helping against waiting,
  // and dropping that row would let a composer outage move the restraint ratio (MAR-64).
  if (row.decisionFailure && !(row.judgment && row.judgment.kind)) return false;
  if (row.eligibility && row.eligibility.kind !== 'eligible') return false;

  // A deterministic contract never weighed helping against waiting — the chat fast path and
  // the comparison handler synthesise `help` without asking the model at all. Counting one as
  // an agent choice would mean a busier inbound chat volume LOWERED measured restraint with no
  // change in the agent's judgment at all, which is the opposite of what the ratio is for.
  // This is what the `source` tag exists to do; reading it here is the whole of its job.
  if (row.judgment && row.judgment.source && row.judgment.source !== 'agent') return false;

  const { judgment, ambiguous } = normalizeDecisionRead(row);
  if (ambiguous || !judgment) return false;
  return judgment === 'help' || judgment === 'wait';
}

/**
 * What a USED sale claim rested on (MAR-69) — recorded on the decision row as
 * `saleEvidenceBasis`. The execution-time re-check judges a claim only against its own basis:
 * `offer` (a speakable offer's code or announcement, or offers were the only sale evidence in
 * context), `markdown` (an item's compare-at price), `history` (a just-bought paid price).
 * Only `offer` has a live counterpart the re-check can read, so only `offer` is re-checked.
 */
const SALE_EVIDENCE_BASES = Object.freeze(['offer', 'markdown', 'history']);

module.exports = {
  SALE_EVIDENCE_BASES,
  JUDGMENT_KINDS,
  JUDGMENT_SOURCES,
  REPLY_TURN_TRIGGERS,
  DISPOSITION_KINDS,
  DISPOSITION_SOURCES,
  FEASIBILITY_REASONS,
  SELF_RESTRAINT_REASONS,
  LIFECYCLE_REASONS,
  AUTHORITY_REASONS,
  MODE_REASONS,
  DEDUPLICATION_REASONS,
  BLOCKED_REASONS,
  ELIGIBILITY_KINDS,
  INELIGIBLE_REASONS,
  PRE_JUDGMENT_BLOCK_REASONS,
  DECISION_FAILURE_STAGES,
  DECISION_FAILURE_CODES,
  LEGACY_AMBIGUOUS,
  LEGACY_HELP_ACTION,
  WITHHELD_STATUSES,
  SENT_STATUSES,
  normalizeDecisionRead,
  isHelpDecision,
  isReplyTurnDecision,
  countsTowardRestraint
};
